<script lang="ts">
    import type { CardDto, CardsetDto } from "@app/models/flashcardsApi.model";
    import {
        getFavoriteTranslations,
        removeFavoriteTranslation,
    } from "@app/services/flashcards.service";
    import {
        COMMON__Filter_cards,
        COMMON__loading,
        COMMON__notFound,
        ERROR__networkError__message,
        FAVORITE__Favorite_translations,
        MY_GLOSBE_FAVORITE_TRANSLATIONS__Loading_favorite_translations,
        SPEECH__tryAgain,
    } from "@app/services/i18n.messages";
    import { getMessage } from "@app/services/i18n.service";
    import BrokenHeartIcon from "../../Icons/BrokenHeartIcon.svelte";
    import Loading from "../../Icons/Loading.svelte";
    import CardsetDetailsHeader from "../CardsetDetailsHeader.svelte";
    import { removeTranslationFromFavorites } from "../FlashcardsIntegration.service";
    import {
        homeBreadcrumbItem,
        myFavoriteTranslationsBreadcrumbItem,
        myGlosbeBreadcrumbItem,
        setBreadcrumbs,
    } from "@app/components/Breadcrumbs/breadcrumbs.store";
    import TrashIcon from "@app/components/Icons/TrashIcon.svelte";

    export let l1;
    export let l2;

    let cardset: CardsetDto;
    let lastFilterValue = "";

    function filterCards(filterValue: string) {
        lastFilterValue = filterValue;
        cardsToShow = cardset.cards || [];
        if (filterValue.length > 0) {
            cardsToShow = cardsToShow.filter((card: CardDto) => {
                let matchesSide1 =
                    card.side1?.text?.toLowerCase()?.indexOf(filterValue) ?? -1;
                let matchesSide2 =
                    card.side2?.text?.toLowerCase().indexOf(filterValue) ?? -1;
                return matchesSide1 >= 0 || matchesSide2 >= 0;
            });
        }
    }

    let cardsToShow: CardDto[] = [];

    function onFilterInput(event: Event) {
        const input = event.target as HTMLInputElement;

        let filterValue = input.value.trim().toLowerCase();
        filterCards(filterValue);
    }

    const cardsetWithFavoriteTranslationsPromise = getFavoriteTranslations({
        l1,
        l2,
    });
    cardsetWithFavoriteTranslationsPromise.then((fetchedCardset) => {
        cardset = fetchedCardset;
        filterCards("");
        updateBreadcrumb();
    });

    async function unlikeTranslation(event: Event) {
        const targetElement = event.target as HTMLButtonElement;
        const cardIndex = parseInt(targetElement.dataset["cardIndex"]);
        if (
            cardIndex < 0 ||
            isNaN(cardIndex) ||
            cardsToShow.length <= cardIndex
        ) {
            return;
        }

        const card = cardsToShow[cardIndex];
        const resp = await removeTranslationFromFavorites({
            l1: "en",
            l2: "pl",
            phrase: card.side1.text,
            translation: card.side2.text,
        });
        if (resp.status === 200) {
            cardset.cards = [...cardset.cards].filter(
                (cardsetCard) => cardsetCard !== card
            );
            filterCards(lastFilterValue);
        }
    }

    function updateBreadcrumb() {
        setBreadcrumbs([
            homeBreadcrumbItem(),
            myGlosbeBreadcrumbItem(),
            myFavoriteTranslationsBreadcrumbItem(),
            {
                text:
                    cardset && cardset.title
                        ? cardset.title
                        : getMessage(COMMON__loading),
                link: `/user/favoriteTranslations?l1=${l1}&l2=${l2}`,
            },
        ]);
    }
    updateBreadcrumb();
</script>

<svelte:head>
    <title
        >{cardset && cardset.title
            ? cardset.title
            : getMessage(
                  MY_GLOSBE_FAVORITE_TRANSLATIONS__Loading_favorite_translations
              )} | Glosbe</title
    >
</svelte:head>

{#await cardsetWithFavoriteTranslationsPromise}
    <Loading />
{:then cardset}
    <div class="max-w-2xl p-2 m-auto">
        <CardsetDetailsHeader {cardset} />

        <div class="mt-4">
            <h2>{getMessage(FAVORITE__Favorite_translations)}</h2>
            <ul class="gap-1 flex flex-col">
                <li class="w-full">
                    <div class="">
                        <input
                            type="search"
                            placeholder={getMessage(COMMON__Filter_cards)}
                            on:input={onFilterInput}
                            class="bg-gray-50 border border-gray-300 text-gray-900 text-lg rounded focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                        />
                    </div>
                </li>
                {#if cardsToShow.length === 0}
                    <p class="text-center p-4">
                        {getMessage(COMMON__notFound)}
                    </p>
                {/if}
                {#each cardsToShow as card, index (card.id)}
                    <li
                        class="w-full border-slate-300 bg-white border rounded items-center flex overflow-hidden break-words items-stretch"
                    >
                        <div class="flex flex-1">
                            <div class="flex-1 p-2">
                                <p class="text-slate-400 text-xs uppercase">
                                    {cardset.languageCode1}
                                </p>
                                <p>
                                    {card.side1.text}
                                </p>
                            </div>
                            <div
                                class="mx-0 border-r border-b md:border-b-0 border-slate-300 border-dashed"
                            >
                                <div class="" />
                            </div>
                            <div class="flex-1 p-2">
                                <p class="text-slate-400 text-xs uppercase">
                                    {cardset.languageCode2}
                                </p>
                                <p>
                                    {card.side2.text}
                                </p>
                            </div>
                        </div>
                        <div
                            class="flex flex-col md:flex-row items-end md:justify-end bg-slate-200 p-2 rounded-b md:rounded-b-0 md:rounded-r md:items-center"
                        >
                            <button
                                on:click={unlikeTranslation}
                                data-card-index={index}
                                class="text-slate-700"
                            >
                                <TrashIcon />
                            </button>
                        </div>
                    </li>
                {/each}
            </ul>
        </div>
    </div>
{:catch}
    {getMessage(ERROR__networkError__message)}
    <button class="button-sm button-primary" on:click={window.location.reload}>
        {getMessage(SPEECH__tryAgain)}
    </button>
{/await}
